import React from "react";
import { connect } from "react-redux";
import { Container, Generic, Poetry } from "./styles";
import ShareWithFriends from "./social";

import { Books } from "../../../models";
import { EVT_CLICK, EVT_SCROLL } from "../../../lib";
import { DocMeta, setWindowTitle, shareOnFacebook } from "../../../helpers";
import AmountRead from "./AmountRead";
import { transformContents } from "./helpers";
import { eventEmitter } from "../../../services";
import { scrollToTop } from "../../../lib/dom";
import { isLoggedIn, isMobile, pageShareUrl } from "../../../helpers";
import { scrollPercentage } from "../../../lib/dom/scroll";
import Invite from "./Invite";
import { AppState } from "../../../store";
import { Page } from "../../../pages/BookPage/TableOfContents";
import { AxiosResponse } from "axios";

const TYPE_POETRY = "POETRY";

const mapState = ({ user, reader }: AppState) => ({
    user,
    //@ts-ignore
    size: reader.get("size") as string,
    //@ts-ignore
    theme: reader.get("theme") as string,
});

type StateProps = ReturnType<typeof mapState>;

export type ReaderDoc = {
    id: number;
    name: string;
    skip_pdf: boolean;
    type: string;
    visibility: string;
    updated_at: string;

    pages: Page[];
    doc_meta: DocMeta[];
};

type OwnProps = {
    doc_id: number;
    page_id: number;
    doc: ReaderDoc;
};
type Props = StateProps & OwnProps;
type State = {
    counter: number;
    contents: string;
};

class PageContents extends React.Component<Props, State> {
    private elText: React.RefObject<HTMLDivElement>;

    constructor(props: Props) {
        super(props);
        this.state = {
            counter: -1,
            contents: "",
        };

        this.elText = React.createRef<HTMLDivElement>();
    }

    pageId() {
        const {
            doc: { pages },
            page_id,
        } = this.props;
        return page_id || pages[0]["id"];
    }

    async componentDidMount() {
        await this.fetch();
        window.addEventListener(EVT_CLICK, this.onImgClick);
        window.addEventListener(EVT_SCROLL, this.onScroll);
    }

    componentWillUnmount() {
        window.removeEventListener(EVT_CLICK, this.onImgClick);
        window.removeEventListener(EVT_SCROLL, this.onScroll);
    }

    async componentDidUpdate(prevProps: Props) {
        if (this.props.page_id !== prevProps.page_id) {
            await this.fetch();
        }
    }

    sendingScroll = false;
    pendingScrollEvt = false;
    onScroll = () => {
        if (this.sendingScroll) {
            this.pendingScrollEvt = true;
            return;
        }

        this.sendingScroll = true;
        this.sendScrollEvt();

        setTimeout(() => {
            this.sendingScroll = false;
            if (this.pendingScrollEvt) {
                this.sendScrollEvt();
                this.pendingScrollEvt = false;
            }
        }, 2 * 1000);
    };

    sendScrollEvt() {
        const value = scrollPercentage().toFixed(0);
        const event_label = `${this.currentPage().id}-${value}`;

        const { gtag } = window;
        gtag("event", "scroll", {
            event_label,
            event_category: "v-pos",
        });
    }

    onImgClick = (evt: MouseEvent) => {
        if (isMobile()) {
            return;
        }

        const elm = evt.target as HTMLElement;
        if (!elm) {
            return;
        }

        if (elm.parentNode && elm.parentNode.nodeName === "A") {
            return;
        }

        if (
            elm.nodeName.toLowerCase() === "img" &&
            (elm as HTMLImageElement).naturalWidth >= 250
        ) {
            eventEmitter.emit(
                "pageContents.showImg",
                (elm as HTMLImageElement).src
            );
        }
    };

    scrollToStart = () => {
        scrollToTop(window);
    };

    fetching = false;
    async fetch() {
        try {
            if (this.fetching) {
                return;
            }

            this.fetching = true;

            const {
                doc_id,
                doc: { pages },
            } = this.props;
            const page_id = this.pageId();

            const { data }: AxiosResponse<string> = await Books.fetch(
                `${doc_id}/contents/${page_id}`
            );

            const name = pages.filter((p) => p.id === page_id)[0]["name"];
            const contents = transformContents(data);

            const { counter } = this.state;
            this.setState({ contents, counter: counter + 1 }, () => {
                this.scrollToStart();
            });

            this.fetching = false;
            setWindowTitle(`${name} - ${this.props.doc.name}`);
        } catch (e) {
            this.setState({ contents: "" }, this.scrollToStart);
            this.fetching = false;

            console.warn("fetch contents", e);
        }
    }

    html() {
        return {
            __html: this.state.contents,
        };
    }

    currentPage() {
        const { doc, page_id } = this.props;
        return doc.pages.filter((p) => p.id === page_id)[0];
    }

    getShareUrl() {
        const { doc } = this.props;
        return pageShareUrl(doc, this.currentPage());
    }

    logShareEvent(type: string) {
        const { doc, page_id } = this.props;
        const { gtag } = window;
        gtag("event", "share", {
            event_category: "page",
            event_label: `${type}-${doc.id}-${page_id}`,
        });
    }

    onWhatsAppShare = (evt: React.MouseEvent<HTMLDivElement>) => {
        evt.preventDefault();
        evt.stopPropagation();

        this.logShareEvent("facebook");
        shareOnFacebook(this.getShareUrl());
        return false;
    };

    share() {
        if (!this.elText || !this.elText.current) {
            return null;
        }

        return (
            <ShareWithFriends
                doc={this.props.doc}
                counter={this.state.counter}
                onClick={this.onWhatsAppShare}
            />
        );
    }

    invite() {
        if (isLoggedIn(this.props.user)) {
            return null;
        }

        return <Invite counter={this.state.counter} />;
    }

    render() {
        const {
            size,
            theme,
            doc: { type, visibility },
        } = this.props;

        const isPublic = visibility === "PUBLIC";

        if (type === TYPE_POETRY) {
            return (
                <Container>
                    <Poetry
                        ref={this.elText}
                        size={size}
                        theme={theme}
                        dangerouslySetInnerHTML={this.html()}
                    />
                    {isPublic && this.share()}
                    {isPublic && this.invite()}
                    <AmountRead />
                </Container>
            );
        }

        return (
            <Container>
                <Generic
                    ref={this.elText}
                    size={size}
                    theme={theme}
                    dangerouslySetInnerHTML={this.html()}
                />
                {isPublic && this.share()}
                {isPublic && this.invite()}
                <AmountRead />
            </Container>
        );
    }
}

export default connect(mapState)(PageContents);
